import React, { useEffect } from 'react';
import { connect } from 'redux-bundler-react';
import { Accordion, Alert } from '@trussworks/react-uswds';

import StepHeader from '@components/step-header/StepHeader';
import Tooltip from '@components/tooltip/tooltip';
import InternalLink from '@components/internal-link/InternalLink';
import ExternalLink from '@components/external-link/ExternalLink';
import LinkButton from '@components/link/linkButton';

import { FileTypes, ResourceFiles } from '@src/utils/enums';
import { examplePlansFileList } from '@src/app-pages/Resources/components/Permitting/PermittingHelper';
import { useRequest } from '@src/stores/request.store';

export const GPForm2bInstructionsMetadata = {
  header: 'Request General Permit',
  sectionName: 'Instructions',
};

const tooltipContent = {
  PGPTooltip:
    'Nationwide Permits (NWPs), Regional General Permits (RGPs), and Programmatic General Permits (PGPs) are types of permits whose purpose is to authorize activities that the USACE has determined to be substantially similar in nature (such as utility lines, residential developments, bank stabilization, etc.) and which cause minimal individual and cumulative environmental impacts. Regional General Permits can authorize activities within a limited geographic area (such as a county or state), a specific region (such as a group of contiguous states), or the entire nation (Nationwide General Permits).',
  RegionalConditionsTooltip:
    'Regional Conditions are District-specific conditions that apply to Nationwide Permits within the geographic area of responsibility of the USACE District where the project is located. Regional Conditions account for regional issues to ensure that the NWP activity will not result in more than minimal adverse environmental effects either individually or cumulatively.',
  WOTUSTooltip:
    'Waters of the United States that are permanently adversely affected by filling, flooding, excavation, or drainage because of the regulated activity. The loss of stream bed includes the acres of stream bed that are permanently adversely affected by filling or excavation because of the regulated activity. Permanent adverse effects include permanent discharges of dredged or fill material that change an aquatic area to dry land, increase the bottom elevation of a waterbody, or change the use of a waterbody. The acreage of loss of waters of the United States is a threshold measurement of the impact to jurisdictional waters or wetlands for determining whether a project may qualify for an NWP; it is not a net threshold that is calculated after considering compensatory mitigation that may be used to offset losses of aquatic functions and services. Waters of the United States temporarily filled, flooded, excavated, or drained, but restored to pre-construction contours and elevations after construction, are not included in the measurement of loss of waters of the United States. Impacts resulting from activities that do not require Department of the Army authorization, such as activities eligible for exemptions under Section 404(f) of the Clean Water Act, are not considered when calculating the loss of waters of the United States.',
};

const faqAccordion = [
  {
    id: '1',
    headingLevel: 'h3',
    title: 'When can I start work?',
    content: (
      <>
        <p>The applicant shall not begin the proposed activity until either: </p>
        <p>
          1. He or she is notified in writing by the USACE District that the activity may proceed under the NWP with any
          special conditions imposed by the district or division engineer; or{' '}
        </p>
        <p>
          2. 45 calendar days have passed from the USACE District's receipt of the complete PCN and the applicant has
          not received written notice from the district or division engineer. However, if the permittee was required to
          notify USACE pursuant to general condition 18 that listed species or critical habitat might be affected or are
          in the vicinity of the activity, or to notify USACE pursuant to general condition 20 that the activity might
          have the potential to cause effects to historic properties, the permittee cannot begin the activity until
          receiving written notification from USACE that there is "no effect" on listed species or "no potential to
          cause effects" on historic properties, or that any consultation required under Section 7 of the Endangered
          Species Act (see 33 CFR 330.4(f)) and/or Section 106 of the National Historic Preservation Act (see 33 CFR
          330.4(g)) has been completed. If the proposed activity requires a written waiver to exceed specified limits of
          an NWP, the permittee may not begin the activity until the USACE District issues the waiver. If the district
          or division engineer notifies the permittee in writing that an Individual Permit is required within 45
          calendar days of receipt of a complete PCN, the permittee cannot begin the activity until an Individual Permit
          has been issued. Subsequently, the permittee's right to proceed under the NWP may be modified, suspended, or
          revoked only in accordance with the procedure set forth in 33 CFR 330.5(d)(2).{' '}
        </p>
      </>
    ),
  },
  {
    id: '2',
    headingLevel: 'h3',
    title: 'What does permanent "loss" of waters mean?',
    content: (
      <p>
        Waters of the United States that are permanently adversely affected by filling, flooding, excavation, or
        drainage because of the regulated activity. The loss of stream bed includes the acres of stream bed that are
        permanently adversely affected by filling or excavation because of the regulated activity. Permanent adverse
        effects include permanent discharges of dredged or fill material that change an aquatic area to dry land,
        increase the bottom elevation of a waterbody, or change the use of a waterbody. The acreage of loss of waters of
        the United States is a threshold measurement of the impact to jurisdictional waters or wetlands for determining
        whether a project may qualify for an NWP; it is not a net threshold that is calculated after considering
        compensatory mitigation that may be used to offset losses of aquatic functions and services. Waters of the
        United States temporarily filled, flooded, excavated, or drained, but restored to pre-construction contours and
        elevations after construction, are not included in the measurement of loss of waters of the United States.
        Impacts resulting from activities that do not require Department of the Army authorization, such as activities
        eligible for exemptions under Section 404(f) of the Clean Water Act, are not considered when calculating the
        loss of waters of the United States.{' '}
      </p>
    ),
  },
  {
    id: '3',
    headingLevel: 'h3',
    title: 'How does the USACE decide whether to allow my project to use a Nationwide Permit?',
    content: (
      <>
        <p>
          The USACE District will make the decision whether to allow the use of a specific Nationwide Permit as
          follows:{' '}
        </p>
        <p>
          1. In reviewing the PCN for the proposed activity, the USACE District will determine whether the activity
          authorized by the NWP will result in more than minimal individual or cumulative adverse environmental effects
          or may be contrary to the public interest. If a project proponent requests authorization by a specific NWP,
          the USACE District should issue the NWP verification for that activity if it meets the terms and conditions of
          that NWP, unless they determine, after considering mitigation, that the proposed activity will result in more
          than minimal individual and cumulative adverse effects on the aquatic environment and other aspects of the
          public interest and exercises discretionary authority to require an Individual Permit for the proposed
          activity. For a linear project, this determination will include an evaluation of the single and complete
          crossings of waters of the United States that require PCNs to determine whether they individually satisfy the
          terms and conditions of the NWP(s), as well as the cumulative effects caused by all of the crossings of waters
          of the United States authorized by an NWP. If an applicant requests a waiver of an applicable limit, as
          provided for in NWPs 13, 36, or 54, the USACE District will only grant the waiver upon a written determination
          that the NWP activity will result in only minimal individual and cumulative adverse environmental
          effects.{' '}
        </p>
        <p>
          2. When making minimal adverse environmental effects determinations the USACE District will consider the
          direct and indirect effects caused by the NWP activity. They will also consider the cumulative adverse
          environmental effects caused by activities authorized by an NWP and whether those cumulative adverse
          environmental effects are no more than minimal. The USACE District will also consider site specific factors,
          such as the environmental setting in the vicinity of the NWP activity, the type of resource that will be
          affected by the NWP activity, the functions provided by the aquatic resources that will be affected by the NWP
          activity, the degree or magnitude to which the aquatic resources perform those functions, the extent that
          aquatic resource functions will be lost as a result of the NWP activity (e.g., partial or complete loss
          <Tooltip name='WOTUSTooltip' content={tooltipContent.WOTUSTooltip} />
          ), the duration of the adverse effects (temporary or permanent), the importance of the aquatic resource
          functions to the region (e.g., watershed or ecoregion), and mitigation required by the USACE District. If an
          appropriate functional or condition assessment method is available and practicable to use, that assessment
          method may be used by the USACE District to assist in the minimal adverse environmental effects determination.
          The USACE District may add case-specific special conditions to the NWP authorization to address site-specific
          environmental concerns.
        </p>
        <p>
          3. If the proposed activity requires a PCN and will result in a loss
          <Tooltip name='WOTUSTooltip' content={tooltipContent.WOTUSTooltip} /> of greater than 1/10 acre of wetlands or
          3/100-acre of stream bed, the applicant should submit a mitigation proposal with the PCN. Applicants may also
          propose compensatory mitigation for NWP activities with smaller impacts, or for impacts to other types of
          waters. The USACE District will consider any proposed compensatory mitigation or other mitigation measures the
          applicant has included in the proposal in determining whether the net adverse environmental effects of the
          proposed activity are no more than minimal. The compensatory mitigation proposal may be either conceptual or
          detailed. If the USACE District determines that the activity complies with the terms and conditions of the NWP
          and that the adverse environmental effects are no more than minimal, after considering mitigation, the USACE
          District will notify the permittee and include any activity-specific conditions in the NWP verification the
          USACE District deems necessary. Conditions for compensatory mitigation requirements must comply with the
          appropriate provisions at 33 CFR 332.3(k). The USACE District must approve the final mitigation plan before
          the permittee commences work in waters of the United States, unless the USACE District determines that prior
          approval of the final mitigation plan is not practicable or not necessary to ensure timely completion of the
          required compensatory mitigation. If the applicant elects to submit a compensatory mitigation plan with the
          PCN, the USACE District will expeditiously review the proposed compensatory mitigation plan. The USACE
          District must review the proposed compensatory mitigation plan within 45 calendar days of receiving a complete
          PCN and determine whether the proposed mitigation would ensure that the NWP activity results in no more than
          minimal adverse environmental effects. If the net adverse environmental effects of the NWP activity (after
          consideration of the mitigation proposal) are determined by the USACE District to be no more than minimal, the
          USACE District will provide a timely written response to the applicant. The response will state that the NWP
          activity can proceed under the terms and conditions of the NWP, including any activity-specific conditions
          added to the NWP authorization by the USACE District.{' '}
        </p>
        <p>
          4. If the USACE District determines that the adverse environmental effects of the proposed activity are more
          than minimal, then the USACE District will notify the applicant either: (a) that the activity does not qualify
          for authorization under the NWP and instruct the applicant on the procedures to seek authorization under an
          Individual Permit; (b) that the activity is authorized under the NWP subject to the applicant's submission of
          a mitigation plan that would reduce the adverse environmental effects so that they are no more than minimal;
          or (c) that the activity is authorized under the NWP with specific modifications or conditions. Where the
          USACE District determines that mitigation is required to ensure no more than minimal adverse environmental
          effects, the activity will be authorized within the 45-day PCN period (unless additional time is required to
          comply with general conditions 18, 20, and/or 31), with activity-specific conditions that state the mitigation
          requirements. The authorization will include the necessary conceptual or detailed mitigation plan or a
          requirement that the applicant submit a mitigation plan that would reduce the adverse environmental effects so
          that they are no more than minimal. When compensatory mitigation is required, no work in waters of the United
          States may occur until the USACE District has approved a specific mitigation plan or has determined that prior
          approval of a final mitigation plan is not practicable or not necessary to ensure timely completion of the
          required compensatory mitigation.{' '}
        </p>
      </>
    ),
  },
];

const GPForm2bInstructions = connect('doDownloadFile', ({ doDownloadFile, stepNo }) => {
  const updateSection = useRequest((s) => s.updateSection);
  const districtReqAccordion = [
    {
      title: 'Project Description',
      content: (
        <p>
          You must include a description of the proposed activity; the activity's purpose; direct and indirect adverse
          environmental effects the activity would cause, including the anticipated amount of loss
          <Tooltip name='WOTUSTooltip' content={tooltipContent.WOTUSTooltip} /> of wetlands, other special aquatic
          sites, and other waters expected to result from the NWP activity, in acres, linear feet, or other appropriate
          unit of measure; a description of any proposed mitigation measures intended to reduce the adverse
          environmental effects caused by the proposed activity; and any other NWP(s), Regional General Permit(s), or
          Individual Permit(s) used or intended to be used to authorize any part of the proposed project or any related
          activity, including other separate and distant crossings for linear projects that require Department of the
          Army authorization but do not require pre-construction notification. The description of the proposed activity
          and any proposed mitigation measures should be sufficiently detailed to allow the USACE District to determine
          that the adverse environmental effects of the activity will be no more than minimal and to determine the need
          for compensatory mitigation or other mitigation measures. For single and complete linear projects (roadways,
          utility lines, etc.), the PCN must include the quantity of anticipated losses
          <Tooltip name='WOTUSTooltip' content={tooltipContent.WOTUSTooltip} /> of wetlands, other special aquatic
          sites, and other waters for each single and complete crossing of those wetlands, other special aquatic sites,
          and other waters.
        </p>
      ),
      id: '1',
      headingLevel: 'h3',
    },
    {
      title: 'Project Plans',
      content: (
        <>
          <p>
            Sketches/plans should be provided when necessary to show that the activity complies with the terms of the
            applicable NWP. This information can greatly facilitate timely reviews and decisions by USACE.
            Sketches/plans should contain sufficient detail to provide an illustrative description of the proposed
            activity (e.g., a conceptual plan), but do not need to be detailed engineering plans.{' '}
          </p>
          <p>
            While plans do not need to be professionally produced, they should be clear, accurate, and contain all
            necessary information. The example plans included in these instructions could be used as a guide when
            developing project plans. Following these examples will help facilitate an efficient review of the
            application and prevent the need for the USACE to request additional information.
          </p>
          <p className='text-bold'>Example Plans:</p>
          {examplePlansFileList.map((file, index) => (
            <p key={index} className={`margin-bottom-0 ${index !== 0 && 'margin-top-0'}`}>
              <LinkButton onClick={() => doDownloadFile(file.filename, FileTypes.Resource)} content={file.label} />
            </p>
          ))}
        </>
      ),
      id: '2',
      headingLevel: 'h3',
    },
    {
      title: 'Delineation of Waters of the United States',
      content: (
        <p>
          The PCN must include a delineation of wetlands, other special aquatic sites, and other waters, such as lakes
          and ponds, and perennial, intermittent, and ephemeral streams, on the project site. Wetland delineations must
          be prepared in accordance with the current method required by USACE. The permittee may ask USACE to delineate
          the special aquatic sites and other waters on the project site, but there may be a delay if USACE does the
          delineation, especially if the project site is large or contains many wetlands, other special aquatic sites,
          and other waters. While it is the role of the USACE to identify{' '}
          <InternalLink href='/home/jurisdiction' content='WOTUS' openInNewTab /> on a project site, many USACE
          Districts do not have the staff and capacity for conducting delineations, especially if the project site is
          large or contains many wetlands, other special aquatic sites, and other waters. Therefore, in these cases, it
          is generally recommended that you voluntarily retain an environmental consulting firm to prepare a delineation
          report to submit to the USACE on your behalf.
          <span className='text-bold'>
            {' '}
            Please contact your local{' '}
            <ExternalLink href='https://regulatory.ops.usace.army.mil/offices/' content='USACE Regulatory office' /> to
            determine if they conduct delineations and to confirm current processing times.{' '}
          </span>
          Furthermore, the 45-day Nationwide Permit review period will not start until the delineation has been
          submitted to or completed by USACE, as appropriate.
        </p>
      ),
      id: '3',
      headingLevel: 'h3',
    },
    {
      title: 'Mitigation',
      content: (
        <p>
          The activity must be designed and constructed to avoid and minimize adverse effects, both temporary and
          permanent, to waters of the United States to the maximum extent practicable at the project site (i.e., on
          site). If the proposed activity will result in the loss
          <Tooltip name='WOTUSTooltip' content={tooltipContent.WOTUSTooltip} /> of greater than 1/10-acre of wetlands or
          3/100-acre of stream bed and a PCN is required, the applicant must submit a statement describing how the
          mitigation requirement will be satisfied, or explaining why the adverse environmental effects are no more than
          minimal and why compensatory mitigation should not be required. As an alternative, the applicant may submit a
          conceptual or detailed{' '}
          <ExternalLink
            href='https://www.usace.army.mil/Missions/Civil-Works/Regulatory-Program-and-Permits/mitig_info/'
            content='mitigation plan'
          />
          .{' '}
        </p>
      ),
      id: '4',
      headingLevel: 'h3',
    },
    {
      title: 'Threatened and Endangered Species',
      content: (
        <p>
          For non-federal permittees, if any listed species (or species proposed for listing) or designated critical
          habitat (or critical habitat proposed for such designation) might be affected or is in the vicinity of the
          activity, or if the activity is located in designated critical habitat (or critical habitat proposed for such
          designation), the PCN must include the name(s) of those endangered or threatened species (or species proposed
          for listing) that might be affected by the proposed activity or utilize the designated critical habitat (or
          critical habitat proposed for such designation) that might be affected by the proposed activity. For NWP
          activities that require pre-construction notification, Federal permittees must provide documentation
          demonstrating compliance with the Endangered Species Act.{' '}
        </p>
      ),
      id: '5',
      headingLevel: 'h3',
    },
    {
      title: 'Historic Properties and Cultural Resources',
      content: (
        <p>
          For non-federal permittees, if the NWP activity might have the potential to cause effects to a historic
          property listed on, determined to be eligible for listing on, or potentially eligible for listing on, the
          National Register of Historic Places, the PCN must state which historic property might have the potential to
          be affected by the proposed activity or include a vicinity map indicating the location of the historic
          property. For NWP activities that require pre-construction notification, Federal permittees must provide
          documentation demonstrating compliance with Section 106 of the National Historic Preservation Act.{' '}
        </p>
      ),
      id: '6',
      headingLevel: 'h3',
    },
    {
      title: 'Wild and Scenic Rivers',
      content: (
        <p>
          For an activity that will occur in a component of the National Wild and Scenic River System, or in a river
          officially designated by Congress as a "study river" for possible inclusion in the system while the river is
          in an official study status, the PCN must identify the Wild and Scenic River or the "study river" (see general
          condition 16).{' '}
        </p>
      ),
      id: '7',
      headingLevel: 'h3',
    },
    {
      title: 'Federally Authorized Civil Works Projects',
      content: (
        <p>
          For an NWP activity that requires permission from, or review by, USACE pursuant to 33 U.S.C. 408 because it
          will alter or temporarily or permanently occupy or use a U.S. Army Corps of Engineers federally authorized
          civil works project, the preconstruction notification must include a statement confirming that the project
          proponent has submitted a written request for Section 408 permission from, or review by, the District office
          having jurisdiction over that USACE project.{' '}
        </p>
      ),
      id: '8',
      headingLevel: 'h3',
    },
  ];

  useEffect(() => {
    updateSection(stepNo, { valid: true, label: GPForm2bInstructionsMetadata.sectionName });
  }, [updateSection, stepNo]);

  return (
    <>
      <StepHeader text='Instructions' />

      <p>
        Nationwide Permits (NWPs) are a type of general permit issued on a nationwide basis to streamline the
        authorization of certain activities in{' '}
        <InternalLink href='/home/jurisdiction' content='Waters of the United States (WOTUS)' openInNewTab /> that
        result in no more than minimal individual and cumulative adverse environmental effects to WOTUS. Many Nationwide
        Permits require Pre-Construction Notification (PCN) to the U.S. Army Corps of Engineers (USACE) before
        commencing those activities. This electronic PCN will assist applicants in providing the information required
        for a complete PCN.{' '}
      </p>
      <p>
        This form may also be used in some districts to submit a complete Regional or Programmatic General Permit
        <Tooltip name='PGPTooltip' content={tooltipContent.PGPTooltip} />
      </p>
      <p>
        <ExternalLink
          href='https://usace.contentdm.oclc.org/utils/getfile/collection/p16021coll7/id/20099'
          content='2021 Nationwide Permit List, General Conditions, and Definitions'
        />
      </p>
      <p>
        <ExternalLink
          href='https://www.regulations.gov/search?filter=COE-2020-0002'
          content='2021 Nationwide Permit Regional Conditions'
        />
      </p>
      <p>
        <ExternalLink
          href='https://usace.contentdm.oclc.org/utils/getfile/collection/p16021coll7/id/19757'
          content='2021 Nationwide Permit Summary Chart'
        />
      </p>
      <Alert heading='IMPORTANT' headingLevel='h5' type='warning' noIcon>
        If you're unfamiliar with the Nationwide Permit program and/or local USACE District Regulatory procedures, we
        recommend that you request a pre-application meeting with the local USACE District prior to submitting this
        request. You may also contact your local USACE District to ask general questions and to determine how to
        proceed. Contact information is found at your local{' '}
        <ExternalLink href='https://regulatory.ops.usace.army.mil/offices/' content='USACE Regulatory office' />.
      </Alert>
      <p className='margin-top-2'>
        Pre-application meetings provide the applicant with the information needed to select the correct type of permit
        as well as the information needed for a complete application, including factors which the USACE must consider in
        its decision-making process. Meetings are more productive when the applicant has prepared information regarding
        impacts to <InternalLink href='/home/jurisdiction' content='WOTUS' openInNewTab />, developed a project purpose,
        considered alternatives to reduce impacts to WOTUS, and considered a mitigation statement. This reduces delays
        in application reviews and decisions.
      </p>
      <hr className='margin-bottom-3' />
      <h5>Requirements for All Applications:</h5>
      <p>
        In accordance with Nationwide Permit General Condition 32(b), specific information must be included in the PCN
        before it can be considered complete and before the applicant can proceed with the proposed activity. The USACE
        District will review the PCN to determine if it is complete within 30 calendar days of the date of receipt and,
        if the PCN is determined to be incomplete, notify the applicant within that 30-day period to request the
        additional information necessary to make the PCN complete. As a general rule, districts will request additional
        information necessary to make the PCN complete only once. However, if the applicant does not provide all the
        requested information, then the USACE District will notify the applicant that the PCN is still incomplete and
        the PCN review process will not commence until all of the requested information has been received by the USACE
        District.
      </p>
      <p>
        The expandable sections below detail the requirements for a complete Preconstruction Notification (PCN) Form.
        This is provided for information purposes, since the electronic PCN will guide you through providing information
        necessary for a complete application.
      </p>
      <Alert heading='IMPORTANT' headingLevel='h5' type='warning' noIcon>
        Districts may have District-specific requirements for complete applications by regional conditions
        <Tooltip name='RegionalConditionsTooltip' content={tooltipContent.RegionalConditionsTooltip} />. Please consult
        the local District's regional conditions on
        <ExternalLink
          href='https://www.regulations.gov/search?filter=%3A%20COE-2020-0002.'
          content=' Regulations.gov'
        />
      </Alert>

      <Accordion
        bordered
        items={districtReqAccordion}
        multiselectable={true}
        className='margin-top-3 margin-bottom-3 padding-left-2'
      />

      <h5>Frequently Asked Questions:</h5>
      <Accordion bordered items={faqAccordion} multiselectable={true} className='padding-left-2 margin-top-2' />
      <div className='margin-top-2'>
        <LinkButton
          onClick={() => doDownloadFile(ResourceFiles.Reg101FreqUsedTerms, FileTypes.Resource)}
          title='RRS Commonly Used Regulatory Terms [PDF, 9 pages]'
          content='RRS Commonly Used Regulatory Terms [PDF, 9 pages]'
        />
      </div>
    </>
  );
});

GPForm2bInstructions.metadata = GPForm2bInstructionsMetadata;

export default GPForm2bInstructions;
